import { LvfVehicleBrakeType } from '../../../enum/supplychain/lvf-vehicle-brake-type';
import { LvfVehicleKilometerSource } from '../../../enum/supplychain/lvf-vehicle-kilometer-source';
import { LvfVehicleNationalCategory } from '../../../enum/supplychain/lvf-vehicle-national-category';
import { EnumHelper } from '../../../helper/enum.helper';
import { Guid } from '../../../types/guid.type';
import { Equipment, IEquipmentInfo } from './equipment';
import type { IEquipmentAssignmentInfo, EquipmentAssignment } from './equipment-assignment';
import { instantiateEquipmentAssignment } from './equipment-lazy-instantiation';
import { MultiStatusConfig } from '../../multi-status/multi-status-config';
import { IMultiStatusConfigInfo } from '../../multi-status/multi-status-config';
import { MultiStatus } from '../../multi-status/multi-status';
import { IMultiStatusInfo } from '../../multi-status/multi-status';
import { IPersonInfo, PersonInfo } from '../../core/person-info';

export interface IEquipmentVehicleInfo extends IEquipmentInfo {
  RegistrationNumber?: string;
  AuthorUid?: Guid;
  Author?: IPersonInfo;
  Version?: string;
  RegistrationDateString?: string;
  CurrentKilometerDateString?: string;
  WheelDimensionUid?: Guid;
  AxelBrandUid?: Guid;
  LvfVehicleKilometerSource?: LvfVehicleKilometerSource;
  LvfVehicleNationalCategory?: LvfVehicleNationalCategory;
  LvfVehicleBrakeType?: LvfVehicleBrakeType;

  KilometerOnEntryValue?: number;
  FuelTankCapacity?: number;
  IsRenting?: boolean;
  IsLastControlOk?: boolean;
  HasBeenReturned?: boolean;

  LastControlDate?: Date;
  LastControlDateString?: string;

  Equipment?: IEquipmentInfo;
  FiscalHorsepower?: number;
  DirectBookingAllowed?: boolean;
  DirectBookingAllowedConfig?: MultiStatusConfig;
  IdentificationInfo?: string;
  CurrentAssignment?: IEquipmentAssignmentInfo;
  KilometersUntilMaintenance?: number;
}

export class EquipmentVehicle extends Equipment {
  override AuthorUid?: Guid;
  override Author?: PersonInfo;
  Version?: string;
  RegistrationDateString?: string;
  RegistrationDateDisplayed?: string;
  CurrentKilometerDateString?: string;
  CurrentKilometerDateDisplayed?: string;
  ParkingEntryDateDisplayed?: string;

  WheelDimensionUid?: Guid;
  AxelBrandUid?: Guid;
  LvfVehicleKilometerSource?: LvfVehicleKilometerSource;
  LvfVehicleNationalCategory?: LvfVehicleNationalCategory;
  LvfVehicleBrakeType?: LvfVehicleBrakeType;
  RegistrationDate?: Date;
  CurrentKilometerDate?: Date;
  KilometerOnEntryValue?: number;
  FuelTankCapacity?: number;
  IsLastControlOk?: boolean;
  LastControlDate?: Date;
  LastControlDateString?: string = '';
  LastControlDateDisplayed?: string;

  // Propriétés Date pour les dates
  Equipment?: Equipment;
  FiscalHorsepower?: number;
  override DirectBookingAllowed?: boolean;
  DirectBookingAllowedConfig?: MultiStatusConfig;
  override CurrentAssignment?: EquipmentAssignment;
  override KilometersUntilMaintenance?: number;

  // Clientside calculated properties
  LvfVehicleKilometerSourceLabel?: string;
  LvfVehicleNationalCategoryLabel?: string;
  LvfVehicleBrakeTypeLabel?: string;

  constructor(info: IEquipmentVehicleInfo) {
    super(info);

    this.AuthorUid = info.AuthorUid;
    this.Author = PersonInfo.createInstance(PersonInfo, info.Author);
    this.Version = info.Version;
    this.RegistrationDateString = info.RegistrationDateString;
    this.CurrentKilometerDateString = info.CurrentKilometerDateString;
    this.WheelDimensionUid = info.WheelDimensionUid;
    this.AxelBrandUid = info.AxelBrandUid;
    this.LvfVehicleKilometerSource = info.LvfVehicleKilometerSource;
    this.LvfVehicleNationalCategory = info.LvfVehicleNationalCategory;
    this.LvfVehicleBrakeType = info.LvfVehicleBrakeType;
    this.KilometerOnEntryValue = info.KilometerOnEntryValue;
    this.FuelTankCapacity = info.FuelTankCapacity;
    this.IsLastControlOk = info.IsLastControlOk;
    this.LastControlDate = info.LastControlDate;
    this.FiscalHorsepower = info.FiscalHorsepower;
    this.DirectBookingAllowed = info.DirectBookingAllowed ?? false;
    this.CurrentAssignment = info.CurrentAssignment
      ? instantiateEquipmentAssignment(info.CurrentAssignment)
      : undefined;
    this.KilometersUntilMaintenance = info.KilometersUntilMaintenance;

    this.DirectBookingAllowedConfig = this.buildDirectBookingAllowedConfig();
    this.changeDirectBookingAllowedStyle();

    this.LvfVehicleKilometerSourceLabel = EnumHelper.getTranslationKey('LvfVehicleKilometerSource', LvfVehicleKilometerSource, this.LvfVehicleKilometerSource);
    this.LvfVehicleNationalCategoryLabel = EnumHelper.getTranslationKey('LvfVehicleNationalCategory', LvfVehicleNationalCategory, this.LvfVehicleNationalCategory);
    this.LvfVehicleBrakeTypeLabel = EnumHelper.getTranslationKey('LvfVehicleBrakeType', LvfVehicleBrakeType, this.LvfVehicleBrakeType);
  }

  /**
   * Retourne les noms des propriétés de date pour le formatage automatique
   */
  override getDatePropertyNames(): string[] {
    return [...super.getDatePropertyNames(), 'RegistrationDate', 'CurrentKilometerDate'];
  }

  buildDirectBookingAllowedConfig(): MultiStatusConfig {
    return new MultiStatusConfig(<IMultiStatusConfigInfo>{
      Statuses: [
        new MultiStatus(<IMultiStatusInfo>{
          Label: 'app.vehicle.directBookingAllowed.reservable',
          Style: 'color: var(--dropdown-bg); background-color: var(--purple-success)',
          Handler: () => {
            this.DirectBookingAllowed = true;
            this.changeDirectBookingAllowedStyle();
          },
        }),
        new MultiStatus(<IMultiStatusInfo>{
          Label: 'app.vehicle.directBookingAllowed.notReservable',
          Style: 'color: var(--module-title); background-color: var(--dropdown-bg)',
          Handler: () => {
            this.DirectBookingAllowed = false;
            this.changeDirectBookingAllowedStyle();
          },
        }),
      ],
    });
  }

  changeDirectBookingAllowedStyle(): void {
    if (!this.DirectBookingAllowedConfig) return;

    if (this.DirectBookingAllowed) {
      this.DirectBookingAllowedConfig.Statuses[0].Style = 'color: var(--dropdown-bg); background-color: var(--purple-success)';
      this.DirectBookingAllowedConfig.Statuses[1].Style = 'color: var(--module-title); background-color: var(--dropdown-bg)';
    } else {
      this.DirectBookingAllowedConfig.Statuses[0].Style = 'color: var(--module-title); background-color: var(--dropdown-bg)';
      this.DirectBookingAllowedConfig.Statuses[1].Style = 'color: var(--dropdown-bg); background-color: #C6C8D3';
    }
  }
}
